/**
 * 封裝 design-canvas 的 design store，統一處理客製化程度以及作品上下架檢查以及總價處理等
 * */

import {
  AppendElementCommand,
  type BarcodeElementData,
  CompositeCommand,
  createBarcodeElement,
  createImageBackground,
  createPhotoElement,
  createPlainColorBackground,
  createStickerElement,
  type DesignCommand,
  type DesignConfig,
  type ImageBackgroundData,
  type ImmutableStickerElement,
  isImageBackground,
  isPlainColorBackground,
  isStickerElement,
  isTextElement,
  type PhotoElementData,
  type PointOrVector2D,
  type Rect,
  RemoveElementCommand,
  SetBackgroundCommand,
  type StickerElementData,
  useDesignStore,
} from '@evolutivelabs/amuse-design-canvas'
import { t } from 'i18next'
import { defineStore, storeToRefs } from 'pinia'
import { computed, readonly, ref, shallowRef, watch } from 'vue'

import { CASE_MATERIAL_IMAGE_SIZE } from '@/components/designArea/utils'
import { isBoundDeviceState } from '@/components/newProductPanel/newProductPanel.type'
import {
  type CollageWithDesignInfoCommonFieldsFragment,
  type DesignGroupType,
} from '@/graphql/creator-platform/generated'
import { convertArtworkI18ns } from '@/utils/artworkI18ns'
import { loadImage } from '@/utils/loadImage'

import { useCurrencyStore } from './currencyStore'
import { useDesignGroupStore } from './designGroupStore'
import { useNewProductStore } from './newProductStore'
import { type CheckoutInfo, type Material, type OrderLineItem, type Writable } from './pricePlan'
import { usePricePlansStore } from './pricePlansStore'

export const useDittoDesignStore = defineStore('dittoDesignStore', () => {
  const designStore = useDesignStore()
  const pricePlansStore = usePricePlansStore()
  const productStore = useNewProductStore()
  const currencyStore = useCurrencyStore()
  const { currentCountryCode } = storeToRefs(currencyStore)

  const { clear, updateBackground, importConfig, clearDesign } = designStore
  const design = computed(() => designStore.design)

  const checkoutInfo = shallowRef<CheckoutInfo | null>(null)
  const { productState } = storeToRefs(productStore)
  const isEligibleForCheckout = computed(
    () =>
      designStore.design.background?.isVisible === true ||
      [...designStore.design.elements.values()].some((item) => item.isVisible),
  )

  const isAllTextRendered = computed(() =>
    [...designStore.design.elements.values()].every(
      (x): boolean => !isTextElement(x) || x.isRendered,
    ),
  )

  // Template Mode flag: 當啟用時，跳過 artwork 價格查詢
  // 用於 IP Collab Template，因為 template 只有產品定價，沒有 artwork 定價
  const isTemplateMode = ref(false)
  const templateInfo = ref<null | {
    displayId: string
    ipPageHandle: string
    ipPageName: string
    name: string
    seriesCode: string
  }>(null)
  const originalTemplateDesignState = ref<null | { configJson: DesignConfig }>(null)

  watch(
    [design, productState, currentCountryCode],
    async () => {
      const updateStartedAt = Date.now()
      const product = productState.value?.selectedProduct ?? null
      if (product === null) {
        checkoutInfo.value = null
        return
      }

      const deviceTitle = isBoundDeviceState(productStore.productState)
        ? productStore.productState.device.name
        : null

      const productTitle = product.title.fullProductTitle
      const materials: Material[] = []
      materials.push({
        imageUrl: product.productSet.thumbnailUrl,
        subtitle: null,
        title: deviceTitle === null ? productTitle : `${deviceTitle} - ${productTitle}`,
        price: product.variant.price,
      })

      const currentDesign = design.value
      const { background, elements } = currentDesign
      const stickers = [...elements.values()].filter(
        (e): e is ImmutableStickerElement => isStickerElement(e) && e.isVisible,
      )

      const ids = [...new Set(stickers.map((v) => v.groupId))]
      if (background !== null && isImageBackground(background) && background.isVisible) {
        ids.push(background.id)
      }

      const priceInfoMap = await pricePlansStore.getPriceByArtworkIds(ids)

      const accumulator = {
        price: product.variant.price,
        compareAtPrice: product.variant.compareAtPrice,
      }

      const lineItems = new Map<string, Writable<OrderLineItem>>()
      const stickerCounter = new Map<ImmutableStickerElement['groupId'], number>()
      const stickerMaterials: Array<
        Material & { readonly groupId: ImmutableStickerElement['groupId'] }
      > = []
      const stickerGroupIds = new Set<string>()
      for (const sticker of stickers) {
        const p = priceInfoMap.get(sticker.groupId)
        if (p === undefined) {
          designStore.execute(new RemoveElementCommand(sticker), {
            ignoreHistory: true,
          })
        } else {
          stickerCounter.set(sticker.groupId, (stickerCounter.get(sticker.groupId) ?? 0) + 1)
          if (!stickerGroupIds.has(sticker.groupId)) {
            accumulator.price += p.price
            accumulator.compareAtPrice += p.compareAtPrice
            const currItem = lineItems.get(p.sku)
            if (currItem === undefined) {
              lineItems.set(p.sku, { quantity: 1, handle: p.handle })
            } else {
              currItem.quantity++
            }
            stickerMaterials.push({
              groupId: sticker.groupId,
              imageUrl: sticker.imageUrl,
              title: sticker.displayName,
              subtitle: null, // 待會換掉
              price: p.price,
            })
            stickerGroupIds.add(sticker.groupId)
          }
        }
      }
      materials.push(
        ...stickerMaterials.map((s) => ({
          ...s,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          subtitle: t('support.sticker_count', { count: stickerCounter.get(s.groupId)! }),
        })),
      )

      if (background?.isVisible === true) {
        if (isImageBackground(background) && !isTemplateMode.value) {
          const p = priceInfoMap.get(background.id)
          if (p === undefined) {
            designStore.execute(new SetBackgroundCommand(null), { ignoreHistory: true })
          } else {
            accumulator.price += p.price
            accumulator.compareAtPrice += p.compareAtPrice
            const currItem = lineItems.get(p.sku)
            if (currItem === undefined) {
              lineItems.set(p.sku, { quantity: 1, handle: p.handle })
            } else {
              currItem.quantity++
            }
            materials.push({
              imageUrl: background.thumbnailUrl,
              price: p.price,
              title: background.displayName,
              subtitle: t('support.bg_count', { count: 1 }),
            })
          }
        } else {
          materials.push({
            imageUrl: background.thumbnailUrl,
            price: null,
            title: background.displayName,
            subtitle: null,
          })
        }
      }

      for (const element of elements.values()) {
        if (!isStickerElement(element)) {
          materials.push({
            imageUrl: element.thumbnailUrl,
            title: element.displayName,
            price: null,
            subtitle: null,
          })
        }
      }

      if (checkoutInfo.value !== null && checkoutInfo.value.updateStartedAt > updateStartedAt) {
        return
      }

      checkoutInfo.value = {
        ...accumulator,
        lineItems,
        materials,
        priceWithCurrency: currencyStore.formatDisplayPrice(accumulator.price),
        seriesCode: templateInfo.value?.seriesCode,
        updateStartedAt,
      }
    },
    { immediate: true },
  )

  const viewableRect = computed(
    () => productStore.productState?.selectedProduct?.product.viewableRect ?? null,
  )

  async function addPhoto(data: Omit<PhotoElementData, 'position'>): Promise<void> {
    await productStore.init()
    if (viewableRect.value === null) {
      throw new Error('viewableRect is null')
    }

    const { position, scaleRatio } = calculateImageParams(
      data.width,
      data.height,
      viewableRect.value,
    )
    designStore.appendElement(createPhotoElement({ ...data, position, scaleRatio }))
  }

  async function addBarcode(data: Pick<BarcodeElementData, 'imageUrl' | 'name'>): Promise<void> {
    await productStore.init()
    if (viewableRect.value === null) {
      throw new Error('viewableRect is null')
    }
    const { width, height } = await loadImage(data.imageUrl)
    const { position, scaleRatio } = calculateImageParams(width, height, viewableRect.value)
    designStore.appendElement(
      createBarcodeElement({ ...data, position, scaleRatio, width, height }),
    )
  }

  async function addSticker(
    data: Omit<StickerElementData, 'height' | 'position' | 'width'>,
  ): Promise<void> {
    await productStore.init()
    if (viewableRect.value === null) {
      throw new Error('viewableRect is null')
    }

    const { width, height } = await loadImage(data.imageUrl)
    const { scaleRatio, position } = calculateImageParams(width, height, viewableRect.value)
    designStore.appendElement(
      createStickerElement({
        ...data,
        width,
        height,
        position,
        scaleRatio,
      }),
    )
  }

  function removeEmptyTextElements(): void {
    const emptyTextElements = [...designStore.design.elements.values()].filter(
      (x) =>
        isTextElement(x) &&
        x.content.trim().length === 0 &&
        (x.backgroundColor ?? 'transparent') === 'transparent',
    )
    for (const elements of emptyTextElements) {
      const command = new RemoveElementCommand(elements)
      designStore.execute(command)
    }
  }

  const { getDesignGroup } = useDesignGroupStore()

  async function updateImageBackground(
    data: Omit<ImageBackgroundData, 'backgroundType' | 'height' | 'position' | 'width'>,
    designGroupType: DesignGroupType,
  ): Promise<void> {
    const { designRect } = await getDesignGroup(designGroupType)

    designStore.updateBackground(
      createImageBackground({
        id: data.id,
        imageUrl: data.imageUrl,
        creatorId: data.creatorId,
        name: data.name,
        width: designRect.width,
        height: designRect.height,
        position: {
          x: designRect.x,
          y: designRect.y,
        },
        isVisible: true,
      }),
    )
  }

  function deleteBackground(): void {
    updateBackground(null)
  }

  function updatePlainColorBackground(colorCode: string): void {
    if (colorCode === TRANSPARENT_COLOR_CODE) {
      deleteBackground()
    } else {
      updateBackground(
        createPlainColorBackground({
          position: { x: 0, y: 0 },
          width: CASE_MATERIAL_IMAGE_SIZE,
          height: CASE_MATERIAL_IMAGE_SIZE,
          colorCode,
          isVisible: true,
          name: t('support.plain_color_background'),
        }),
      )
    }
  }

  const backgroundPlainColor = computed((): string | undefined => {
    const currentDesign = design.value
    const element = currentDesign.background
    if (element === null || !isPlainColorBackground(element)) {
      return undefined
    }
    return element.colorCode
  })

  async function convertCollageAndAppendToStore(
    collage: CollageWithDesignInfoCommonFieldsFragment,
    clearHistory = true,
  ): Promise<void> {
    const designStore = useDesignStore()
    const commands: DesignCommand[] = []
    if (clearHistory) {
      clear()
    } else {
      clearDesign()
    }
    const background =
      collage.background === null || collage.background === undefined
        ? collage.background
        : convertArtworkI18ns(collage.background)
    if (typeof background?.imageUrl === 'string') {
      const designGroupStore = useDesignGroupStore()
      const designGroup = await designGroupStore.getDesignGroup(collage.designGroupType)
      commands.push(
        new SetBackgroundCommand(
          createImageBackground({
            id: background.id,
            width: designGroup.designRect.width,
            height: designGroup.designRect.height,
            creatorId: collage.creator.id,
            position: {
              x: designGroup.designRect.x,
              y: designGroup.designRect.y,
            },
            imageUrl: background.imageUrl,
            name: background.name,
            isVisible: collage.backgroundVisible,
          }),
        ),
      )
    }
    const stickerOnCollages = collage.stickerOnCollages.sort((a, b) => a.zIndex - b.zIndex)
    for (const stickerOnCollage of stickerOnCollages) {
      const {
        x,
        y,
        sticker,
        visible,
        angleRadian,
        size,
        numStackColumns,
        numStackRows,
        stackGapRatio,
        stackOffsetRatio,
      } = stickerOnCollage
      commands.push(
        new AppendElementCommand(
          createStickerElement({
            stickerId: sticker.id,
            groupId: sticker.group.id,
            creatorId: collage.creator.id,
            isLocked: false,
            isVisible: visible,
            position: {
              x,
              y,
            },
            width: size,
            height: size,
            angleRadian,
            scaleRatio: 1,
            imageUrl: sticker.imageUrl,
            thumbnailUrl: sticker.thumbnailUrl ?? sticker.imageUrl,
            name: sticker.group.name,
            numStackColumns,
            numStackRows,
            stackGapRatio,
            stackOffsetRatio,
          }),
        ),
      )
    }

    designStore.execute(new CompositeCommand(commands))
  }

  function enableTemplateMode(info: {
    displayId: string
    ipPageHandle: string
    ipPageName: string
    name: string
    seriesCode: string
  }): void {
    isTemplateMode.value = true
    templateInfo.value = info
  }

  function disableTemplateMode(): void {
    isTemplateMode.value = false
    templateInfo.value = null
  }

  function saveTemplateOriginalState(): void {
    const configJson = designStore.exportConfig()
    originalTemplateDesignState.value = { configJson }
  }

  function resetToTemplateOriginalState(): void {
    if (!isTemplateMode.value || originalTemplateDesignState.value === null) {
      console.warn(
        'Cannot reset to original template state: not in template mode or original state not saved',
      )
      return
    }

    const { configJson } = originalTemplateDesignState.value
    designStore.importConfig(configJson)
  }

  return {
    updatePlainColorBackground,
    addSticker,
    addBarcode,
    addPhoto,
    updateImageBackground,
    deleteBackground,
    removeEmptyTextElements,
    backgroundPlainColor,
    // TODO 檢查上下架和客製化程度
    importConfig,
    convertCollageAndAppendToStore,
    checkoutInfo,
    isEligibleForCheckout,
    isTemplateMode,
    templateInfo: readonly(templateInfo),
    enableTemplateMode,
    disableTemplateMode,
    saveTemplateOriginalState,
    resetToTemplateOriginalState,
    isAllTextRendered,
  }
})

function calculateImageParams(
  width: number,
  height: number,
  rect: Rect,
): { position: PointOrVector2D; scaleRatio: number } {
  const expectedMaxWidth = rect.width * 0.6
  const expectedMaxHeight = rect.height * 0.6
  const widthScale = width > expectedMaxWidth ? expectedMaxWidth / width : 1
  const heightScale = height > expectedMaxHeight ? expectedMaxHeight / height : 1
  const scaleRatio = Math.min(widthScale, heightScale)
  const position = {
    x: (rect.width - width * scaleRatio) / 2 + rect.x,
    y: (rect.height - height * scaleRatio) / 2 + rect.y,
  }
  return { scaleRatio, position }
}

export const TRANSPARENT_COLOR_CODE = 'transparent'
